import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  buttonBaseClasses,
  FormControlLabel,
  Paper,
  Switch,
  SxProps,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import { distanceEquals } from '@paragon/geometry-library';
import { PropertiesPanel } from '@paragon/layout-models';

import { getCurrentTransformationTargets } from '../../../../library/transformations/selectors';
import {
  doesTransformableTypeSupportLevelReassignment,
  transformationTypeToPresentParticiple,
} from '../../../../library/transformations/types';
import {
  beginTransformation,
  cancelTransformation,
  enableTransformationLevelReassignment,
} from '../../../../store/actions/layout/transformationActions';
import { getDistanceFormatterSelector } from '../../../../store/selectors/getDistanceFormatterSelector';
import { createGetLevelGuids, getAllLevelsDictionary } from '../../../../store/selectors/layoutDataSelectors';
import { getLayoutHierarchyAccessLevel } from '../../../../store/selectors/layoutModeSelectors';
import {
  transformationComponentLinksPreservationToggled,
  transformationDuplicationToggled,
  transformationFeaturePatternContainerGuidSet,
  transformationLevelGuidSet,
  transformationLevelReassignmentDisabled,
} from '../../../../store/slices/applicationSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { ProjectHierarchyContextSelects } from '../../subpanels/properties/ProjectHierarchyContextSelects';
import { MirrorControls } from './MirrorControls';
import { MovementControls } from './MovementControls';
import { RotationControls } from './RotationControls';

export function TransformPanel() {
  const dispatch = useAppDispatch();

  const hierarchyAccess = useAppSelector(getLayoutHierarchyAccessLevel);
  const getLevelGuids = useAppSelector(createGetLevelGuids);

  const transformation = useAppSelector((state) => state.application.transformingObjectsInfo);
  const transformationTargets = useAppSelector(getCurrentTransformationTargets);

  if (transformation == null || transformationTargets == null) return null;

  const currentLevelGuids =
    transformationTargets.type === 'workLine' ? [] : getLevelGuids(transformationTargets.entities);
  const singleCurrentLevelGuid = currentLevelGuids.length === 1 ? currentLevelGuids[0] : null;

  const supportsLevelReassignment =
    hierarchyAccess.write &&
    doesTransformableTypeSupportLevelReassignment(transformationTargets.type) &&
    currentLevelGuids.length > 0;

  return (
    <PropertiesPanel
      title={
        <ToggleButtonGroup
          exclusive
          sx={{
            marginX: 1,
            [`& .${buttonBaseClasses.root}`]: { paddingX: 3 },
          }}
          value={transformation.type}
          onChange={(_, type) => type && dispatch(beginTransformation(type))}
          aria-label="transformation type"
        >
          <ToggleButton value="movement" aria-label="Move">
            Move
          </ToggleButton>
          <ToggleButton value="rotation" aria-label="Rotate">
            Rotate
          </ToggleButton>
          <ToggleButton value="mirror" aria-label="Mirror">
            Mirror
          </ToggleButton>
        </ToggleButtonGroup>
      }
      sx={{ marginTop: 1 }}
      titleButton={
        <TooltipIconButton
          enableInteractive
          title="Close"
          sx={{ marginRight: 1 }}
          onClick={() => dispatch(cancelTransformation())}
        >
          <CloseIcon />
        </TooltipIconButton>
      }
    >
      <Paper
        elevation={1}
        sx={{ marginX: 1, display: 'flex', flexDirection: 'row', justifyContent: 'center', marginBottom: 1 }}
      >
        <FormControlLabel
          sx={{ paddingX: 3, paddingY: 1, marginRight: 0 }}
          label={`Duplicate before ${transformationTypeToPresentParticiple(transformation.type)}`}
          control={
            <Switch
              checked={transformation.duplicate}
              onChange={() => {
                dispatch(transformationDuplicationToggled());
              }}
            />
          }
        />
      </Paper>

      {transformationTargets.type === 'trussEnvelope' && (
        <Paper
          elevation={1}
          sx={{ marginX: 1, display: 'flex', flexDirection: 'row', justifyContent: 'center', marginBottom: 1 }}
        >
          <FormControlLabel
            sx={{ paddingX: 3, paddingY: 1, marginRight: 0 }}
            label="Keep components linked"
            control={
              <Switch
                checked={transformation.preserveLinkedComponents ?? false}
                onChange={() => {
                  dispatch(transformationComponentLinksPreservationToggled());
                }}
              />
            }
          />
        </Paper>
      )}

      {transformation.preserveLinkedComponents && (
        <Alert variant="outlined" severity="warning" sx={{ marginX: 1, marginBottom: 1 }}>
          There is no guarantee that linked component designs will be valid in their new location. Please review
          carefully after moving.
        </Alert>
      )}

      {supportsLevelReassignment && (
        <Paper
          elevation={1}
          sx={{
            marginX: 1,

            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginBottom: 1,
          }}
        >
          <FormControlLabel
            sx={{ paddingX: 3, paddingY: 1, marginRight: 0 }}
            label="Change Level / Building"
            control={
              <Switch
                checked={transformation.reassign !== false}
                onChange={(_, checked) => {
                  if (checked) {
                    dispatch(enableTransformationLevelReassignment());
                  } else {
                    dispatch(transformationLevelReassignmentDisabled());
                  }
                }}
              />
            }
          />

          {transformation.reassign && (
            <ProjectHierarchyContextSelects
              sx={{ width: '100%', paddingX: 2, marginBottom: 2 }}
              levelGuid={transformation.reassign.levelGuid}
              onChange={(levelGuid) => {
                if (levelGuid) {
                  dispatch(transformationLevelGuidSet(levelGuid));
                }
              }}
              featurePatternContainer={
                transformation.reassign.featurePatternContainerGuid !== undefined
                  ? {
                      guid: transformation.reassign.featurePatternContainerGuid,
                      onChange: (guid) => dispatch(transformationFeaturePatternContainerGuidSet(guid)),
                    }
                  : undefined
              }
            />
          )}

          {transformation.reassign && (
            <LevelReassignmentElevationDisplay
              sx={{ marginBottom: 2, ['& > *']: { paddingY: 0.5 } }}
              originLevelGuid={singleCurrentLevelGuid}
              destinationLevelGuid={transformation.reassign.levelGuid}
            />
          )}
        </Paper>
      )}

      {transformation.type === 'movement' && (
        <MovementControls targetType={transformationTargets.type} transformation={transformation} />
      )}

      {transformation.type === 'rotation' && (
        <RotationControls targetType={transformationTargets.type} transformation={transformation} />
      )}

      {transformation.type === 'mirror' && (
        <MirrorControls targetType={transformationTargets.type} transformation={transformation} />
      )}
    </PropertiesPanel>
  );
}

function LevelReassignmentElevationDisplay(props: {
  sx?: SxProps<Theme>;

  originLevelGuid: string | null;
  destinationLevelGuid: string | null;
}) {
  const levels = useAppSelector(getAllLevelsDictionary);
  const formatDistance = useAppSelector(getDistanceFormatterSelector);

  if (props.originLevelGuid == null || props.destinationLevelGuid == null) return null;

  const originLevel = levels[props.originLevelGuid];
  const destinationLevel = levels[props.destinationLevelGuid];

  if (originLevel == null || destinationLevel == null) return null;
  if (originLevel.plateElevation == null || destinationLevel.plateElevation == null) return null;
  if (distanceEquals(originLevel.plateElevation, destinationLevel.plateElevation)) return null;

  return (
    <Alert variant="outlined" severity="info" sx={props.sx}>
      {formatDistance(destinationLevel.plateElevation - originLevel.plateElevation)} between plate elevations
    </Alert>
  );
}
